import { makeStyles, Theme } from '@material-ui/core/styles'

/**
 * Styles for ResidentTermsRoot component
 */
type ResidentTermsRootStylesProps = {
    headerHeight: number
    collapsableSelectorHeight: number
}
export const useResidentTermsRootStyles = makeStyles<
    Theme,
    ResidentTermsRootStylesProps
>((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: ({ headerHeight, collapsableSelectorHeight }) =>
            `calc(100vh - ${headerHeight + collapsableSelectorHeight}px)`,
        padding: theme.spacing(2),
    },
    headerRow: {
        display: 'flex',
        flex: 1,
    },
    statTiles: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
    },
    statTile: {
        minWidth: 120,
        padding: theme.spacing(0.75, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        alignItems: 'center',
    },
    statTileInteractive: {
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[2],
        },
    },
    statTileActive: {
        border: `2px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.action.selected,
        boxShadow: theme.shadows[2],
    },
    statLabel: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        fontWeight: 600,
        textTransform: 'uppercase',
    },
    statValue: {
        fontSize: '0.9rem',
        fontWeight: 700,
    },
    statContent: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        whiteSpace: 'nowrap',
    },
    headerControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    actionButton: {
        textTransform: 'none',
        fontWeight: 600,
    },
    actionButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    statsGrid: {
        marginBottom: theme.spacing(2),
    },
}))

export const useResidentTermListViewStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        padding: theme.spacing(2),
    },
    accordionContainer: {
        flex: 1,
        overflow: 'auto',
    },
    accordion: {
        marginBottom: theme.spacing(1),
        '&:before': {
            display: 'none',
        },
    },
    accordionSummary: {
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.spacing(1),
    },
    residentInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flex: 1,
    },
    residentName: {
        fontWeight: 600,
        width: 300,
        flexShrink: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    buildingUnit: {
        color: theme.palette.text.primary,
        fontSize: '0.875rem',
        fontWeight: 600,
        width: 150,
        flexShrink: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    leaseDates: {
        color: theme.palette.text.primary,
        fontSize: '0.875rem',
        fontWeight: 500,
        width: 250,
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    statusIndicator: {
        width: 20,
        height: 20,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(1),
    },
    statusComplete: {
        backgroundColor: theme.palette.success.main,
        color: 'white',
    },
    statusNeedsReview: {
        backgroundColor: theme.palette.warning.main,
        color: 'white',
    },
    statusMissing: {
        backgroundColor: theme.palette.error.main,
        color: 'white',
    },
    statusReviewed: {
        backgroundColor: theme.palette.info.main,
        color: 'white',
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(8),
        color: theme.palette.text.secondary,
    },
    loadingState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(8),
    },
    documentCount: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginLeft: theme.spacing(2),
        padding: theme.spacing(0.5, 1),
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.spacing(0.5),
        border: `1px solid ${theme.palette.divider}`,
    },
    documentCountText: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
}))

export const useDocumentViewerStyles = makeStyles((theme: Theme) => ({
    dialogPaper: {
        minHeight: '80vh',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    dialogContent: {
        padding: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    placeholder: {
        textAlign: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(4),
    },
}))

export const useUnpairedSearchDocumentsTableStyles = makeStyles(
    (theme: Theme) => ({
        tableContainer: {
            marginBottom: theme.spacing(2),
        },
        table: {
            minWidth: 650,
        },
        headerCell: {
            fontWeight: 600,
            backgroundColor: theme.palette.grey[50],
        },
        titleCell: {
            maxWidth: 300,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
        },
        actionButtons: {
            display: 'flex',
            gap: theme.spacing(0.5),
            justifyContent: 'center',
            alignItems: 'center',
        },
    }),
)

export const useResidentTermDocumentsTableStyles = makeStyles(
    (theme: Theme) => ({
        tableContainer: {
            marginBottom: theme.spacing(2),
        },
        table: {
            minWidth: 650,
        },
        headerCell: {
            fontWeight: 600,
            backgroundColor: theme.palette.grey[50],
        },
        selectedRow: {
            backgroundColor: theme.palette.primary.light + '20',
            borderLeft: `3px solid ${theme.palette.primary.main}`,
        },
        unselectedRow: {
            '&:hover $selectButton': {
                opacity: 1,
                pointerEvents: 'auto',
            },
        },
        titleCell: {
            maxWidth: 300,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
        },
        actionButtons: {
            display: 'flex',
            gap: theme.spacing(0.5),
            justifyContent: 'center',
        },
        searchButton: {
            marginTop: theme.spacing(2),
            textAlign: 'center',
        },
        emptyState: {
            padding: theme.spacing(2),
            textAlign: 'center',
        },
        loadingState: {
            padding: theme.spacing(2),
            textAlign: 'center',
        },
        selectCell: {
            width: 48,
        },
        selectButton: {
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 150ms ease-in-out',
        },
    }),
)
